import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  MessageSquareQuote,
  Building2,
  Users,
  Check,
  X,
  Loader2,
  Zap,
  Bell,
  TrendingUp,
  DollarSign,
  Clock,
  Star,
} from 'lucide-react';
import { useUpgradeModal } from '@/contexts/UpgradeModalContext';
import { getPlanById, type PricingPlan } from '@/data/pricingPlans';
import { billingApi } from '@/lib/api';

// Map current plan to recommended upgrade plan
function getRecommendedPlan(currentPlan: string): PricingPlan | undefined {
  switch (currentPlan) {
    case 'free_trial':
      return getPlanById('premium');
    case 'base':
      return getPlanById('premium');
    case 'premium':
      return getPlanById('premium_plus');
    case 'premium_plus':
      return getPlanById('unlimited_owl');
    default:
      return getPlanById('premium');
  }
}

// Context-aware content - really speak to the moment
const limitContent = {
  max_invoices: {
    icon: FileText,
    limitLabel: 'invoices',
    headline: 'Send this invoice now',
    subheadline: 'You did the work. Your client is waiting. Don\'t let a limit stop you from getting paid.',
    features: [
      { icon: Zap, title: 'Unlimited invoices', description: 'Never hit this wall again.' },
      { icon: Bell, title: 'Auto payment reminders', description: 'Get paid without chasing.' },
      { icon: DollarSign, title: 'Faster payments', description: 'Our users get paid 2x faster.' },
    ],
    cta: 'Upgrade & Send Invoice',
    urgency: 'Your invoice is ready to go',
  },
  max_quotes: {
    icon: MessageSquareQuote,
    limitLabel: 'quotes',
    headline: 'Send this quote now',
    subheadline: 'Every hour you wait, your potential client might call someone else. Get this quote out.',
    features: [
      { icon: Zap, title: 'Unlimited quotes', description: 'Quote every opportunity.' },
      { icon: TrendingUp, title: 'Higher win rates', description: 'Fast quotes win more jobs.' },
      { icon: FileText, title: 'One-tap invoicing', description: 'Quote accepted? Invoice instantly.' },
    ],
    cta: 'Upgrade & Send Quote',
    urgency: 'Your quote is ready to send',
  },
  max_businesses: {
    icon: Building2,
    limitLabel: 'businesses',
    headline: 'Add your business',
    subheadline: 'You\'re growing. That\'s a good problem to have. Let\'s get your new business set up.',
    features: [
      { icon: Building2, title: 'Unlimited businesses', description: 'Scale without limits.' },
      { icon: Star, title: 'Separate branding', description: 'Each business, its own identity.' },
      { icon: TrendingUp, title: 'Combined insights', description: 'See everything in one place.' },
    ],
    cta: 'Upgrade & Add Business',
    urgency: 'Your new business awaits',
  },
  max_seats: {
    icon: Users,
    limitLabel: 'team members',
    headline: 'Add your team member',
    subheadline: 'You shouldn\'t have to do everything yourself. Bring on help and focus on what matters.',
    features: [
      { icon: Users, title: 'More team seats', description: 'Build the team you need.' },
      { icon: Clock, title: 'Save hours weekly', description: 'Delegate the busywork.' },
      { icon: Star, title: 'Role permissions', description: 'Right access for each person.' },
    ],
    cta: 'Upgrade & Add Team Member',
    urgency: 'Your team member is ready to join',
  },
};

const animationConfig = {
  backdrop: { bg: 'rgba(0, 0, 0, 0.04)', blur: '8px' },
  duration: 400,
};

export function UpgradeModal() {
  const { isOpen, errorData, closeUpgradeModal } = useUpgradeModal();
  const { businessId } = useParams<{ businessId: string }>();
  const [interval, setInterval] = useState<'monthly' | 'annual'>('annual');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'hidden'>('hidden');

  const config = animationConfig;

  useEffect(() => {
    if (isOpen) {
      setPhase('entering');
      const timer = setTimeout(() => setPhase('visible'), 50);
      return () => clearTimeout(timer);
    } else {
      setPhase('hidden');
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setPhase('exiting');
    setTimeout(() => {
      closeUpgradeModal();
    }, config.duration);
  }, [closeUpgradeModal, config.duration]);

  if (!errorData) return null;

  const recommendedPlan = getRecommendedPlan(errorData.current_plan);
  const showContent = phase !== 'hidden';

  if (!showContent && phase === 'hidden') return null;

  // Handle highest plan case
  if (!recommendedPlan) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          onClick={handleClose}
          className="absolute inset-0"
          style={{
            backgroundColor: phase === 'visible' ? config.backdrop.bg : 'rgba(0, 0, 0, 0)',
            backdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
            WebkitBackdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
            transition: `background-color ${config.duration}ms ease-out, backdrop-filter ${config.duration}ms ease-out`,
          }}
        />
        <div
          className="relative w-full max-w-md"
          style={{
            opacity: phase === 'visible' ? 1 : 0,
            transform: phase === 'visible' ? 'scale(1)' : 'scale(0.98)',
            transition: `opacity ${config.duration}ms ease-out, transform ${config.duration}ms ease-out`,
          }}
        >
          <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden p-8">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                <Zap className="h-7 w-7 text-zinc-700 dark:text-zinc-300" />
              </div>
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">You've reached your limit</h2>
              <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-6">
                You're on our highest plan. Contact support for custom enterprise options.
              </p>
              <Button variant="outline" className="w-full h-11" onClick={handleClose}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get context-aware content
  const content = limitContent[errorData.limit as keyof typeof limitContent] || limitContent.max_invoices;

  // All pricing from pricingPlans.ts
  const monthlyPrice = recommendedPlan.pricing.monthly;
  const annualPrice = recommendedPlan.pricing.annually;
  const price = interval === 'annual' ? annualPrice : monthlyPrice;
  const savingsPercent = monthlyPrice > 0 ? Math.round((1 - annualPrice / monthlyPrice) * 100) : 0;

  const handleUpgrade = async () => {
    if (!businessId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await billingApi.createCheckoutSession(recommendedPlan.id, interval, businessId);
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.error) {
        setError(response.error);
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className="absolute inset-0"
        style={{
          backgroundColor: phase === 'visible' ? config.backdrop.bg : 'rgba(0, 0, 0, 0)',
          backdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          WebkitBackdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          transition: `background-color ${config.duration}ms ease-out, backdrop-filter ${config.duration}ms ease-out`,
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl"
        style={{
          opacity: phase === 'visible' ? 1 : 0,
          transform: phase === 'visible' ? 'scale(1)' : 'scale(0.98)',
          transition: `opacity ${config.duration}ms ease-out, transform ${config.duration}ms ease-out`,
        }}
      >
        <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="grid md:grid-cols-2">
            {/* Left side - Emotional appeal */}
            <div className="px-8 pt-8 pb-6 md:px-10 md:pt-10 md:pb-8 bg-zinc-50 dark:bg-zinc-800/50 flex flex-col">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                  {content.headline}
                </h2>
                <p className="text-zinc-600 dark:text-zinc-400">
                  {content.subheadline}
                </p>
              </div>

              <div className="space-y-5">
                {content.features.map((feature) => (
                  <div key={feature.title} className="flex gap-4">
                    <div className="h-10 w-10 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-5 w-5 text-zinc-700 dark:text-zinc-300" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Limit status */}
              <div className="mt-8 md:mt-auto mb-2 pt-5 border-t border-zinc-200 dark:border-zinc-700">
                <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400 mb-1.5">
                  <span className="uppercase tracking-wide font-medium">
                    {errorData.max - errorData.current} {content.limitLabel.charAt(0).toUpperCase() + content.limitLabel.slice(1)} Remaining
                  </span>
                  <span className="font-semibold text-red-400 dark:text-red-300">
                    {errorData.current} of {errorData.max}
                  </span>
                </div>
                <div className="relative h-2 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                  <div className="h-full bg-red-400 rounded-full" style={{ width: '100%' }} />
                </div>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-2">
                  Upgrade to unlock unlimited {content.limitLabel}
                </p>
              </div>
            </div>

            {/* Right side - Pricing */}
            <div className="px-8 pt-8 pb-6 md:px-10 md:pt-10 md:pb-8 flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
                  {recommendedPlan.name}
                </h3>

                {/* Billing toggle */}
                <Tabs value={interval} onValueChange={(v) => setInterval(v as 'monthly' | 'annual')}>
                  <TabsList className="h-10 w-full">
                    <TabsTrigger value="monthly" className="flex-1">Monthly</TabsTrigger>
                    <TabsTrigger value="annual" className="flex-1">
                      Annual
                      <Badge variant="success" className="ml-1.5 text-[10px] px-1.5 py-0">
                        SAVE {savingsPercent}%
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    ${price}
                  </span>
                  <span className="text-zinc-500 dark:text-zinc-400 ml-1">/mo</span>
                </div>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  {interval === 'annual' ? `$${price * 12} billed annually` : 'billed monthly'}
                </p>
              </div>

              {/* Plan features from pricingPlans.ts */}
              <div className="mb-6 flex-1">
                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-3">
                  Also included
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {recommendedPlan.features.slice(0, 6).map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-auto pt-6">
                <Button
                  onClick={handleUpgrade}
                  disabled={isLoading}
                  className="w-full h-11"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    content.cta
                  )}
                </Button>

                {error && (
                  <p className="text-sm text-red-500 dark:text-red-400 text-center mt-2">{error}</p>
                )}

                <button
                  onClick={handleClose}
                  className="w-full mt-1 py-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
